import type { HeartRiskInput, HeartRiskResult } from "@/lib/heartRisk/types";

type Coefficients = {
  lnAge: number;
  lnAgeSquared: number;
  lnTotalChol: number;
  lnAgeXlnTotalChol: number;
  lnHdl: number;
  lnAgeXlnHdl: number;
  lnTreatedSbp: number;
  lnAgeXlnTreatedSbp: number;
  lnUntreatedSbp: number;
  lnAgeXlnUntreatedSbp: number;
  smoker: number;
  lnAgeXsmoker: number;
  diabetes: number;
  baselineSurvival: number;
  meanRisk: number;
};

const PCE_COEFFICIENTS: Record<string, Coefficients> = {
  "female-black": {
    lnAge: 17.1141,
    lnAgeSquared: 0,
    lnTotalChol: 0.9396,
    lnAgeXlnTotalChol: 0,
    lnHdl: -18.9196,
    lnAgeXlnHdl: 4.4748,
    lnTreatedSbp: 29.2907,
    lnAgeXlnTreatedSbp: -6.4321,
    lnUntreatedSbp: 27.8197,
    lnAgeXlnUntreatedSbp: -6.0873,
    smoker: 0.6908,
    lnAgeXsmoker: 0,
    diabetes: 0.8738,
    baselineSurvival: 0.9533,
    meanRisk: 86.6081,
  },
  "female-other": {
    lnAge: -29.799,
    lnAgeSquared: 4.884,
    lnTotalChol: 13.54,
    lnAgeXlnTotalChol: -3.114,
    lnHdl: -13.578,
    lnAgeXlnHdl: 3.149,
    lnTreatedSbp: 2.019,
    lnAgeXlnTreatedSbp: 0,
    lnUntreatedSbp: 1.957,
    lnAgeXlnUntreatedSbp: 0,
    smoker: 7.574,
    lnAgeXsmoker: -1.665,
    diabetes: 0.661,
    baselineSurvival: 0.9665,
    meanRisk: -29.18,
  },
  "female-white": {
    lnAge: -29.799,
    lnAgeSquared: 4.884,
    lnTotalChol: 13.54,
    lnAgeXlnTotalChol: -3.114,
    lnHdl: -13.578,
    lnAgeXlnHdl: 3.149,
    lnTreatedSbp: 2.019,
    lnAgeXlnTreatedSbp: 0,
    lnUntreatedSbp: 1.957,
    lnAgeXlnUntreatedSbp: 0,
    smoker: 7.574,
    lnAgeXsmoker: -1.665,
    diabetes: 0.661,
    baselineSurvival: 0.9665,
    meanRisk: -29.18,
  },
  "male-black": {
    lnAge: 2.469,
    lnAgeSquared: 0,
    lnTotalChol: 0.302,
    lnAgeXlnTotalChol: 0,
    lnHdl: -0.307,
    lnAgeXlnHdl: 0,
    lnTreatedSbp: 1.916,
    lnAgeXlnTreatedSbp: 0,
    lnUntreatedSbp: 1.809,
    lnAgeXlnUntreatedSbp: 0,
    smoker: 0.549,
    lnAgeXsmoker: 0,
    diabetes: 0.645,
    baselineSurvival: 0.8954,
    meanRisk: 19.5425,
  },
  "male-other": {
    lnAge: 12.344,
    lnAgeSquared: 0,
    lnTotalChol: 11.853,
    lnAgeXlnTotalChol: -2.664,
    lnHdl: -7.99,
    lnAgeXlnHdl: 1.769,
    lnTreatedSbp: 1.797,
    lnAgeXlnTreatedSbp: 0,
    lnUntreatedSbp: 1.764,
    lnAgeXlnUntreatedSbp: 0,
    smoker: 7.837,
    lnAgeXsmoker: -1.795,
    diabetes: 0.658,
    baselineSurvival: 0.9144,
    meanRisk: 61.18,
  },
  "male-white": {
    lnAge: 12.344,
    lnAgeSquared: 0,
    lnTotalChol: 11.853,
    lnAgeXlnTotalChol: -2.664,
    lnHdl: -7.99,
    lnAgeXlnHdl: 1.769,
    lnTreatedSbp: 1.797,
    lnAgeXlnTreatedSbp: 0,
    lnUntreatedSbp: 1.764,
    lnAgeXlnUntreatedSbp: 0,
    smoker: 7.837,
    lnAgeXsmoker: -1.795,
    diabetes: 0.658,
    baselineSurvival: 0.9144,
    meanRisk: 61.18,
  },
};

const CATEGORY_BOUNDS = {
  low: 5,
  borderline: 7.5,
  intermediate: 20,
};

function normalizeRace(race: HeartRiskInput["race"]): "white" | "black" | "other" {
  if (race === "black" || race === "white") return race;
  return "other";
}

function getRiskCategory(risk: number): HeartRiskResult["category"] {
  if (risk < CATEGORY_BOUNDS.low) return "low";
  if (risk < CATEGORY_BOUNDS.borderline) return "borderline";
  if (risk < CATEGORY_BOUNDS.intermediate) return "intermediate";
  return "high";
}

function roundRisk(value: number): number {
  return Math.round(Math.max(0, Math.min(100, value)) * 10) / 10;
}

function calculateTenYearRisk(input: HeartRiskInput): number {
  const key = `${input.sex}-${normalizeRace(input.race)}`;
  const coef = PCE_COEFFICIENTS[key];
  const lnAge = Math.log(input.age);
  const lnAgeSquared = lnAge * lnAge;
  const lnTotalChol = Math.log(input.totalCholesterol);
  const lnHdl = Math.log(input.hdlCholesterol);
  const lnSbp = Math.log(input.systolicBp);

  const treatedSbpTerm = input.onHypertensionTreatment ? lnSbp : 0;
  const untreatedSbpTerm = input.onHypertensionTreatment ? 0 : lnSbp;

  const sum =
    coef.lnAge * lnAge +
    coef.lnAgeSquared * lnAgeSquared +
    coef.lnTotalChol * lnTotalChol +
    coef.lnAgeXlnTotalChol * lnAge * lnTotalChol +
    coef.lnHdl * lnHdl +
    coef.lnAgeXlnHdl * lnAge * lnHdl +
    coef.lnTreatedSbp * treatedSbpTerm +
    coef.lnAgeXlnTreatedSbp * lnAge * treatedSbpTerm +
    coef.lnUntreatedSbp * untreatedSbpTerm +
    coef.lnAgeXlnUntreatedSbp * lnAge * untreatedSbpTerm +
    coef.smoker * Number(input.smoker) +
    coef.lnAgeXsmoker * lnAge * Number(input.smoker) +
    coef.diabetes * Number(input.diabetes);

  const risk = 1 - Math.pow(coef.baselineSurvival, Math.exp(sum - coef.meanRisk));
  return roundRisk(risk * 100);
}

function calculateLifetimeRisk(input: HeartRiskInput): number | null {
  if (input.age < 20 || input.age > 59) return null;

  const majorRiskCount = [
    input.totalCholesterol >= 240,
    input.systolicBp >= 160 || input.diastolicBp >= 100 || input.onHypertensionTreatment,
    input.smoker,
    input.diabetes,
  ].filter(Boolean).length;

  const hasElevated = input.totalCholesterol >= 200 || input.systolicBp >= 140 || input.diastolicBp >= 90;
  const hasNotOptimal = input.systolicBp >= 120 || input.diastolicBp >= 80;

  if (input.sex === "male") {
    if (majorRiskCount >= 2) return 69;
    if (majorRiskCount === 1) return 50;
    if (hasElevated) return 46;
    if (hasNotOptimal) return 36;
    return 5;
  }

  if (majorRiskCount >= 2) return 50;
  if (majorRiskCount === 1) return 39;
  if (hasElevated) return 39;
  if (hasNotOptimal) return 27;
  return 8;
}

export function calculatePceRisk(input: HeartRiskInput): HeartRiskResult {
  const tenYearRisk = calculateTenYearRisk(input);
  const optimalTenYearRisk = calculateTenYearRisk({
    ...input,
    totalCholesterol: 170,
    hdlCholesterol: 50,
    systolicBp: 110,
    diastolicBp: 70,
    diabetes: false,
    smoker: false,
    onHypertensionTreatment: false,
  });
  const lifetimeRisk = calculateLifetimeRisk(input);

  return {
    tenYearRisk,
    optimalTenYearRisk,
    lifetimeRisk,
    category: getRiskCategory(tenYearRisk),
  };
}
